/**
 * client/src/utils/consecutiveDays.js
 *
 * THE DISPLAY-ONLY MIRROR of `server/helpers/consecutiveDays.js#runPositions`.
 * 6th/7th-day premium wage feature, Task 6 (the day grid).
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🚨 THE SERVER IS THE AUTHORITY. THIS FILE DECIDES NOTHING ABOUT MONEY.
 * ─────────────────────────────────────────────────────────────────────────
 * Run position is what makes a day a 6th- or 7th-day premium day, and at
 * Stage 3 it is what picks the RATE. That decision is made once, server-side,
 * in `server/helpers/consecutiveDays.js`. This copy exists for ONE reason:
 * the grid has to draw a warning triangle on the day the user is looking at,
 * before any save, and there is no round-trip that can answer "what would
 * position 6 be if I ticked this Saturday?".
 *
 * 🪤 A SECOND IMPLEMENTATION OF A RULE IS A DRIFT HAZARD, so the two are tied
 * together by the OWNER'S WORKED EXAMPLE, reproduced verbatim in BOTH suites
 * (`server/tests/helpers/consecutiveDays.test.js` §"a single day off resets"
 * and `client/src/utils/consecutiveDays.test.js`). Change the rule on either
 * side and the other side's copy of that fixture goes red. If you are here to
 * change the rule, change it on the SERVER first.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * THE RULE (owner ruling 2026-08-20, restated so this file is readable alone)
 * ─────────────────────────────────────────────────────────────────────────
 * Premium depends on CONSECUTIVE WORKED DAYS, never on which calendar day it
 * is. Days 1–5 of a run are standard; day 6 is the 6th-day rate, day 7 the
 * 7th; day 8 restarts the run at 1. **Any day not worked RESETS the run to
 * zero — it does not pause it.** Saturday and Sunday are only the common
 * case. Never implement "Saturday = premium".
 *
 * 🪤 Dates are compared as WHOLE DAYS at LOCAL MIDNIGHT, never by adding
 * 86,400,000ms to a timestamp — see the server file for why. No
 * `toISOString()` anywhere near a date-only value
 * (`server/tests/regressions/no-utc-date-drift.test.js` scans for it).
 */

/**
 * 'YYYY-MM-DD' -> a whole-day number, or null if the string is not a usable
 * date-only value. Mirrors the server's `toDayNumber` in intent: a string
 * that matches the shape but ROLLS OVER ('2026-02-30' -> 2026-03-02) is
 * refused rather than silently landing in a different week.
 *
 * 🟢 Returns null instead of throwing. On the server an unusable row is a
 * money decision and is handled loudly; here it is a pixel, and a grid that
 * blanks one triangle is better than a page that white-screens.
 */
function toDayNumber(dateStr) {
  if (typeof dateStr !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return null;
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  // Round-trip check: catches '2026-02-30' and friends.
  const back = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  if (back !== dateStr) return null;
  return Math.round(d.getTime() / 86400000);
}

/**
 * runPositions(dates) -> Map<'YYYY-MM-DD', number>
 *
 * 1-based run position for every usable date. Input need NOT be sorted (the
 * server's contract requires sorted input and throws otherwise; the grid
 * builds its list from a Set of ticked days, so sorting here is the honest
 * accommodation rather than a behavioural difference — positions are a
 * function of the SET, not of the order it arrives in). Duplicates count once.
 * Unusable values are dropped and simply get no entry.
 */
export function runPositions(dates) {
  const out = new Map();
  if (!Array.isArray(dates)) return out;

  const usable = [];
  const seen = new Set();
  for (const d of dates) {
    if (seen.has(d)) continue;
    const n = toDayNumber(d);
    if (n === null) continue;
    seen.add(d);
    usable.push([d, n]);
  }
  usable.sort((a, b) => a[1] - b[1]);

  let prev = null;
  let pos = 0;
  for (const [dateStr, dayNum] of usable) {
    const consecutive = prev !== null && dayNum - prev === 1;
    pos = consecutive ? (pos % 7) + 1 : 1;
    out.set(dateStr, pos);
    prev = dayNum;
  }
  return out;
}

/** Position 6 and 7 are the premium positions. Nothing else is. */
export const PREMIUM_POSITIONS = [6, 7];

export function isPremiumPosition(pos) {
  return pos === 6 || pos === 7;
}
